/**
 * Keeps a wrapped line aligned the way the row's first line is.
 *
 * Rows across the app are written as `flexWrap="wrap"` (HStack, Flex, Wrap…).
 * Left-aligned rows already wrap to the left, so they are never touched. Two
 * kinds of row would otherwise send right-side items to the left edge:
 *   - `justify="space-between"`: an item that wraps alone sits on the left of its
 *     new line, though it sat at the right end of the first one (e.g. an action
 *     button under a page title). It is pushed back to the right.
 *   - Rows with a growing item (e.g. a `flex="1"` search field) fill their first
 *     line, so the items after it were on the right; their wrapped lines are
 *     right-aligned.
 * Rows that fit on one line are never touched. Components can also do this
 * themselves with `ml="auto"` on the trailing item, which this respects.
 *
 * Rows are left alone when they are column-direction, reversed, or already
 * centered / end-aligned on purpose (pagination, centered button groups). A row
 * can opt out with `data-wrap-align="start"`.
 */

const MARK = "data-wrap-right";
const OPT_OUT_SELECTOR = '[data-wrap-align="start"]';
const ALIGNABLE_JUSTIFY = new Set([
  "normal",
  "flex-start",
  "start",
  "left",
  "space-between",
]);
// Sub-pixel layouts report fractional rects; treat anything within this as touching.
const EPSILON = 1;

let uninstall: (() => void) | null = null;

function isWrappingRow(el: Element, style: CSSStyleDeclaration): boolean {
  if (style.display !== "flex" && style.display !== "inline-flex") return false;
  if (style.flexWrap !== "wrap") return false;
  if (style.flexDirection !== "row") return false;
  if (style.direction === "rtl") return false;
  if (!ALIGNABLE_JUSTIFY.has(style.justifyContent)) return false;
  return !el.closest(OPT_OUT_SELECTOR);
}

function findWrappingRows(root: Element): HTMLElement[] {
  const rows: HTMLElement[] = [];
  const all = root.getElementsByTagName("*");
  for (let i = 0; i < all.length; i += 1) {
    const el = all[i];
    // A row needs at least two items to wrap.
    if (!(el instanceof HTMLElement) || el.childElementCount < 2) continue;
    if (isWrappingRow(el, getComputedStyle(el))) rows.push(el);
  }
  return rows;
}

function inFlowItems(row: HTMLElement): HTMLElement[] {
  const items: HTMLElement[] = [];
  for (const child of Array.from(row.children)) {
    if (!(child instanceof HTMLElement)) continue;
    const style = getComputedStyle(child);
    if (style.display === "none") continue;
    if (style.position === "absolute" || style.position === "fixed") continue;
    items.push(child);
  }
  return items;
}

/** Split into flex lines: a new line starts when an item sits below and back to the left. */
function naturalLines(rects: DOMRect[]): number[][] {
  const lines: number[][] = [];
  rects.forEach((rect, index) => {
    const line = lines[lines.length - 1];
    const prev = line ? rects[line[line.length - 1]] : null;
    if (!prev || (rect.left < prev.right - EPSILON && rect.top > prev.top + EPSILON)) {
      lines.push([index]);
    } else {
      line.push(index);
    }
  });
  return lines;
}

/** Marks the items that start a wrapped line which belongs on the right (measured unmarked). */
function layoutRow(row: HTMLElement, items: HTMLElement[]) {
  if (items.length < 2) return;
  const rects = items.map((item) => item.getBoundingClientRect());
  const lines = naturalLines(rects);
  if (lines.length < 2) return;

  const rowStyle = getComputedStyle(row);
  const contentRight =
    row.getBoundingClientRect().right -
    parseFloat(rowStyle.borderRightWidth) -
    parseFloat(rowStyle.paddingRight);
  const grows = items.some((item) => parseFloat(getComputedStyle(item).flexGrow) > 0);
  // Left-aligned rows already wrap the way their first line reads.
  if (!grows && rowStyle.justifyContent !== "space-between") return;

  for (let l = 1; l < lines.length; l += 1) {
    const line = lines[l];
    // space-between spreads a line of two or more by itself; only a lone item drifts left.
    if (!grows && line.length > 1) continue;
    // Already flush right (full line, or an item with its own auto margin).
    if (rects[line[line.length - 1]].right >= contentRight - EPSILON) continue;
    items[line[0]].setAttribute(MARK, "");
  }
}

export function installWrapRightAlign(): () => void {
  if (typeof document === "undefined" || typeof ResizeObserver === "undefined") {
    return () => {};
  }
  if (uninstall) return uninstall;

  const baseStyle = document.createElement("style");
  baseStyle.textContent = `[${MARK}]{margin-inline-start:auto !important;}`;
  document.head.appendChild(baseStyle);

  let rows: HTMLElement[] = [];
  let rescanQueued = false;
  let frame = 0;

  const resizeObserver = new ResizeObserver(() => schedule(false));

  const clearAll = () => {
    document.querySelectorAll(`[${MARK}]`).forEach((el) => el.removeAttribute(MARK));
  };

  const run = () => {
    frame = 0;
    // Measure every row in its natural layout, then mark them all at once.
    clearAll();
    if (rescanQueued) {
      rescanQueued = false;
      resizeObserver.disconnect();
      rows = findWrappingRows(document.body);
      rows.forEach((row) => resizeObserver.observe(row));
    }
    for (const row of rows) {
      if (row.isConnected) layoutRow(row, inFlowItems(row));
    }
  };

  // Runs before the next paint, so wrapped lines never flash in the wrong place.
  function schedule(rescan: boolean) {
    if (rescan) rescanQueued = true;
    if (!frame) frame = requestAnimationFrame(run);
  }

  const mutationObserver = new MutationObserver(() => schedule(true));
  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ["class", "style", "hidden"],
  });

  // Breakpoints can flip a row's direction or wrap without any DOM mutation.
  const onResize = () => schedule(true);
  window.addEventListener("resize", onResize);

  schedule(true);

  uninstall = () => {
    if (frame) cancelAnimationFrame(frame);
    mutationObserver.disconnect();
    resizeObserver.disconnect();
    window.removeEventListener("resize", onResize);
    clearAll();
    baseStyle.remove();
    uninstall = null;
  };
  return uninstall;
}
